import { Leaf, Drumstick, CupSoda, Droplets } from "lucide-react";
import { Badge, Card, SectionHeading } from "./ui";
import type { MenuCategoryDto, MenuItemDto, MenuPackageDto } from "../lib/api";
import { formatINR } from "../lib/format";

const DIET_META: Record<
  MenuItemDto["dietTag"],
  { label: string; tone: "success" | "warning" | "accent" | "neutral"; icon: typeof Leaf }
> = {
  veg: { label: "Vegetarian", tone: "success", icon: Leaf },
  nonveg: { label: "Non-vegetarian", tone: "warning", icon: Drumstick },
  beverage: { label: "Beverage", tone: "accent", icon: CupSoda },
  water: { label: "Water", tone: "neutral", icon: Droplets },
};

export default function MenuSection({
  categories,
  items,
}: {
  categories: MenuCategoryDto[];
  packages: MenuPackageDto[];
  items: MenuItemDto[];
  expectedAttendees: number;
}) {
  const published = items.filter((i) => i.published);
  const byCategory = categories.map((c) => ({
    category: c,
    items: published.filter((i) => i.categoryId === c.id),
  }));

  return (
    <section id="menu" className="scroll-mt-24 py-24">
      <div className="mx-auto max-w-shell px-5 md:px-10">
        <SectionHeading
          eyebrow="The proposed lunch menu"
          title="14 sections. 21 dishes. One long table."
          description="The committee's complete proposed menu for the Freshers' lunch — vegetarian, non-vegetarian, beverages and water, exactly as proposed. Per-plate vendor pricing is held privately by the committee — the public planning figures live on the Budget page."
        />

        {/* Unlimited highlight */}
        <div className="mt-8 rounded-xl border border-wandor-prompt/40 bg-wandor-prompt/[0.09] p-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-block bg-wandor-prompt px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-black">
              &#10022; Unlimited
            </span>
            <p className="text-[15px] font-medium text-white">
              Everything on this menu is <strong>unlimited</strong> — eat as much as you want.
            </p>
          </div>
          <p className="mt-2 text-sm text-white/65">
            The only limit is time: servings run throughout the lunch window, 2:00 PM – 6:30 PM.
            Come hungry — refills of every dish, soft drinks, and dessert are all included.
          </p>
        </div>

        {/* Official committee menu artwork (source: SHARED/FRESHERS/MENU) */}
        <div className="mt-12">
          <h3 className="text-sm font-medium uppercase tracking-[0.18em] text-wandor-prompt">
            [ OFFICIAL MENU ARTWORK ]
          </h3>
          <Card className="mt-4 overflow-hidden p-3">
            <img
              src="/menu-poster.jpg"
              alt="Official BBA Freshers' Party 2026 menu artwork — 14 sections, 21 dishes, with vegetarian and non-vegetarian markers"
              className="mx-auto max-h-[720px] w-auto max-w-full rounded-md"
              loading="lazy"
            />
            <p className="mt-3 text-center text-xs text-white/50">
              The committee&apos;s official menu design (SHARED/FRESHERS/MENU). Every dish listed
              below matches this artwork.
            </p>
          </Card>
        </div>

        {/* The complete menu */}
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {byCategory.map(({ category, items: catItems }, idx) => (
            <Card key={category.id} className="flex flex-col p-6">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-lg font-medium text-white">{category.name}</h3>
                <span className="font-manrope text-[13px] text-[#AFDDFF]/80">
                  {String(idx + 1).padStart(2, "0")}.
                </span>
              </div>
              {catItems.length === 0 ? (
                <p className="mt-3 text-sm text-white/50">No items recorded in this section yet.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {catItems.map((item) => {
                    const diet = DIET_META[item.dietTag] ?? DIET_META.veg;
                    const DietIcon = diet.icon;
                    return (
                      <li key={item.id} className="border-t border-white/10 pt-3 first:border-t-0 first:pt-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[15px] font-medium text-white">{item.name}</span>
                          <Badge tone={diet.tone}>
                            <DietIcon className="h-3 w-3" aria-hidden="true" />
                            {diet.label}
                          </Badge>
                        </div>
                        {item.description && (
                          <p className="mt-1 text-sm text-white/50">{item.description}</p>
                        )}
                        <p className="mt-1 text-xs text-white/40">
                          {item.perPersonPaise > 0 ? (
                            <span className="text-white/70">{formatINR(item.perPersonPaise)} per plate</span>
                          ) : (
                            <>Price awaiting vendor quotation</>
                          )}
                          {item.vendorRef ? <> · Quote ref: {item.vendorRef}</> : null}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          ))}
        </div>

        <p className="mt-8 text-xs leading-relaxed text-white/40">
          Menu confirmed by the organizing committee. Dish names and section assignments are shown
          exactly as proposed. Dietary labels are indicative — the committee verifies each dish with
          the caterer. Prices are per plate and appear only when a vendor quotation has been recorded.
        </p>
      </div>
    </section>
  );
}
